/* SectorLensChips / SectorLensPanel / SectorInterventionList
 *
 * Calm, enterprise-grade rendering helpers for the new
 * `sector_lens` payload added by the backend sector-profile depth
 * upgrade. Designed to be safe on missing data: every component
 * returns null when its required input is absent or empty.
 */

// ─── Display-only heading mapping ──────────────────────────────────────
// Backend canonical sector "Sport & Grassroots Communities" is rendered
// in the lens header as one of:
//   • "Sport Signal Lens"            — broad (no vectors / multiple vectors)
//   • "Grassroots Sport Signal Lens" — when ONLY the Grassroots vector is selected
// For every other sector the existing "{Sector} lens" copy is preserved.
// This is purely a display mapping — no taxonomy rename, no API change.
const SPORT_SECTOR = 'Sport & Grassroots Communities';
const GRASSROOTS_VECTOR = 'Grassroots & Community Sport';

export function lensHeadingFor(sector, selectedVectors) {
  if (!sector) return 'Sector lens';
  if (sector === SPORT_SECTOR) {
    const vecs = Array.isArray(selectedVectors) ? selectedVectors.filter(Boolean) : [];
    if (vecs.length === 1 && vecs[0] === GRASSROOTS_VECTOR) {
      return 'Grassroots Sport Signal Lens';
    }
    return 'Sport Signal Lens';
  }
  return `${sector} lens`;
}


import React, { useState } from 'react';

const CHIP_BASE = {
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: '10px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  padding: '3px 8px',
  borderRadius: '999px',
  whiteSpace: 'nowrap',
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-secondary)',
  marginRight: '6px',
  marginBottom: '4px',
};

// Per-class colour cues — calm, restrained. Matches existing
// muted enterprise palette. Colour communicates *type* of opportunity,
// never severity (severity remains the existing risk/opportunity bars).
const CLASS_TONE = {
  distress:       { borderColor: 'rgba(180, 60, 60, 0.35)',  color: 'rgb(140, 50, 50)' },
  growth:         { borderColor: 'rgba(60, 130, 90, 0.35)',  color: 'rgb(40, 95, 65)' },
  compliance:     { borderColor: 'rgba(170, 130, 30, 0.35)', color: 'rgb(120, 90, 25)' },
  partnership:    { borderColor: 'rgba(80, 100, 160, 0.35)', color: 'rgb(60, 80, 130)' },
  procurement:    { borderColor: 'rgba(110, 90, 150, 0.35)', color: 'rgb(85, 70, 120)' },
  operational:    { borderColor: 'rgba(80, 110, 130, 0.35)', color: 'rgb(60, 85, 105)' },
  community:      { borderColor: 'rgba(100, 130, 80, 0.35)', color: 'rgb(75, 100, 60)' },
  transformation: { borderColor: 'rgba(120, 95, 145, 0.35)', color: 'rgb(95, 75, 115)' },
};

export function SectorCategoriesChips({ categories, max = 2, testIdPrefix }) {
  if (!Array.isArray(categories) || categories.length === 0) return null;
  const visible = categories.slice(0, max);
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '0' }}>
      {visible.map((cat, i) => (
        <span
          key={i}
          style={CHIP_BASE}
          title={cat}
          data-testid={testIdPrefix ? `${testIdPrefix}-cat-${i}` : undefined}
        >
          {cat.length > 28 ? `${cat.slice(0, 26)}…` : cat}
        </span>
      ))}
    </span>
  );
}

export function SectorOpportunityTypeChips({ opportunityTypes, max = 8, testIdPrefix }) {
  if (!Array.isArray(opportunityTypes) || opportunityTypes.length === 0) return null;
  const visible = opportunityTypes.slice(0, max);
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '0' }}>
      {visible.map((entry, i) => {
        const tone = CLASS_TONE[entry?.class] || {};
        return (
          <span
            key={i}
            style={{ ...CHIP_BASE, ...tone }}
            title={entry?.class ? `${entry.type} • ${entry.class}` : entry?.type || ''}
            data-testid={testIdPrefix ? `${testIdPrefix}-opp-${i}` : undefined}
          >
            {entry?.type}
          </span>
        );
      })}
    </span>
  );
}

/**
 * Compact "Why this matters" panel for Tracker / Pipeline rows.
 * Renders as a small lens summary line, top categories, and
 * opportunity-type chips. Returns null when nothing useful to render.
 *
 * `selectedVectors` (optional) is an array of currently-selected
 * vector names for this sector. Used purely for display-only heading
 * copy on Sport (broad → "Sport Signal Lens"; Grassroots-only →
 * "Grassroots Sport Signal Lens"). Backend canonical sector value is
 * unchanged.
 */
export function SectorLensPanel({ sectorLens, selectedVectors, testIdPrefix = 'sector-lens' }) {
  if (!sectorLens) return null;
  const { sector, lens, categories, opportunity_types: opportunityTypes } = sectorLens;
  const hasContent = lens?.fit_hint || (categories && categories.length) || (opportunityTypes && opportunityTypes.length);
  if (!hasContent) return null;
  const heading = lensHeadingFor(sector, selectedVectors);
  return (
    <div
      data-testid={testIdPrefix}
      style={{
        marginTop: '6px',
        padding: '8px 10px',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: '6px',
      }}
    >
      <div style={{
        fontSize: '10px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'var(--text-secondary)',
        marginBottom: '4px',
      }}>
        {heading}
      </div>
      {lens?.fit_hint && (
        <div style={{ fontSize: '12px', color: 'var(--text)', lineHeight: 1.45, marginBottom: '6px' }}>
          {lens.fit_hint}
        </div>
      )}
      {categories && categories.length > 0 && (
        <div style={{ marginBottom: opportunityTypes?.length ? '4px' : 0 }}>
          <SectorCategoriesChips categories={categories} max={6} testIdPrefix={`${testIdPrefix}-categories`} />
        </div>
      )}
      {opportunityTypes && opportunityTypes.length > 0 && (
        <div>
          <SectorOpportunityTypeChips
            opportunityTypes={opportunityTypes}
            max={6}
            testIdPrefix={`${testIdPrefix}-opps`}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Sector-specific Fix interventions block. Top N visible by default
 * with calm "Show more" expansion. Renders nothing when no
 * interventions are available.
 */
export function SectorInterventionList({ interventions, initialVisible = 3, testIdPrefix = 'sector-interventions' }) {
  const [expanded, setExpanded] = useState(false);
  if (!Array.isArray(interventions) || interventions.length === 0) return null;
  const visible = expanded ? interventions : interventions.slice(0, initialVisible);
  const remaining = interventions.length - visible.length;
  return (
    <div className="panel" style={{ marginBottom: '12px' }} data-testid={testIdPrefix}>
      <div className="panel-header">Sector-specific interventions</div>
      <div className="panel-body" style={{ padding: '4px 8px' }}>
        {visible.map((intv, i) => (
          <div
            key={i}
            data-testid={`${testIdPrefix}-item-${i}`}
            style={{
              padding: '10px 12px',
              borderBottom: i < visible.length - 1 ? '1px solid var(--border)' : 'none',
            }}
          >
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>
              {intv?.title}
            </div>
            {intv?.why_it_matters && (
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '3px', lineHeight: 1.5 }}>
                {intv.why_it_matters}
              </div>
            )}
          </div>
        ))}
      </div>
      {remaining > 0 && (
        <div style={{ padding: '6px 12px', borderTop: '1px solid var(--border)' }}>
          <button
            className="btn btn-outline btn-sm"
            style={{ fontSize: '11px' }}
            onClick={() => setExpanded(true)}
            data-testid={`${testIdPrefix}-show-more`}
          >
            Show {remaining} more
          </button>
        </div>
      )}
      {expanded && interventions.length > initialVisible && (
        <div style={{ padding: '6px 12px', borderTop: '1px solid var(--border)' }}>
          <button
            className="btn btn-outline btn-sm"
            style={{ fontSize: '11px' }}
            onClick={() => setExpanded(false)}
            data-testid={`${testIdPrefix}-show-less`}
          >
            Show less
          </button>
        </div>
      )}
    </div>
  );
}
